'use client'

import { useLayoutEffect, useRef, useState } from 'react'
import { Popover } from '@mantine/core'
import { SearchInputField } from './SearchInputField'
import { SearchSuggestionPanel } from './SearchSuggestionPanel'
import type { SearchTermSuggestion } from './universalSearchTypes'
import type { UseUniversalSearchReturn } from './useUniversalSearch'
import classes from './UniversalSearch.module.css'

const SEARCH_LAYOUT_VARIABLES = [
    '--universal-search-collapsed-height',
    '--universal-search-collapsed-padding',
    '--universal-search-collapsed-gap',
    '--universal-search-collapsed-border-width',
    '--universal-search-icon-size',
    '--universal-search-font-size',
    '--universal-search-line-height',
    '--universal-search-font-weight',
    '--universal-search-focus-outline',
    '--universal-search-focus-outline-offset',
    '--universal-search-suggestions-padding-left',
    '--gm-foreground-fg-primary',
    '--gm-foreground-fg-tertiary',
    '--gm-foreground-fg-placeholder',
    '--gm-foreground-fg-highlight',
] as const

/** Copies inherited search sizing and colors into the document-body portal. */
function readSearchLayoutVariables(field: HTMLElement): React.CSSProperties {
    const styles = getComputedStyle(field)
    const variables = SEARCH_LAYOUT_VARIABLES.flatMap((name) => {
        const value = styles.getPropertyValue(name).trim()
        return value ? [[name, value]] : []
    })

    return Object.fromEntries(variables) as React.CSSProperties
}

const TABBABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',')

function isTabbable(element: HTMLElement) {
    return (
        element.tabIndex >= 0 &&
        element.getClientRects().length > 0 &&
        getComputedStyle(element).visibility !== 'hidden'
    )
}

function findTabbableAroundAnchor(anchor: HTMLElement, dropdown: HTMLElement, backward: boolean) {
    const candidates = Array.from(document.querySelectorAll<HTMLElement>(TABBABLE_SELECTOR)).filter(
        (element) => !anchor.contains(element) && !dropdown.contains(element) && isTabbable(element),
    )
    if (backward) {
        return candidates
            .filter((element) => anchor.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_PRECEDING)
            .pop()
    }
    return candidates.find((element) => anchor.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_FOLLOWING)
}

interface DesktopSearchDropdownProps {
    /** Collapsed search bar that anchors the open panel. */
    target: React.ReactNode
    /** Hint text shown in the search field inside the dropdown. */
    placeholder: string
    ariaLabel?: string
    /** Starter chips shown when the search field is empty. */
    termSuggestions: SearchTermSuggestion[]
    /** Shared search state and handlers from `useUniversalSearch`. */
    search: UseUniversalSearchReturn
    /** Enter submits the search; Escape closes the panel. */
    onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
}

/**
 * Desktop search dropdown anchored to the collapsed search bar via Mantine Popover.
 * The panel overlaps the trigger so the input expands in place. While open,
 * outside clicks, focus changes, and page scrolling close it. Its suggestion
 * list remains scrollable.
 */
export function DesktopSearchDropdown({
    target,
    placeholder,
    ariaLabel,
    termSuggestions,
    search,
    onKeyDown,
}: DesktopSearchDropdownProps) {
    const targetRef = useRef<HTMLDivElement>(null)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const closeSearch = search.close
    const [targetHeight, setTargetHeight] = useState(44)
    const [searchLayoutVariables, setSearchLayoutVariables] = useState<React.CSSProperties>({})
    const [maxHeight, setMaxHeight] = useState<number>()

    useLayoutEffect(() => {
        if (!search.isOpen) return

        const anchor = targetRef.current
        if (!anchor) return

        const rect = anchor.getBoundingClientRect()
        const field = anchor.querySelector('[data-search-collapsed-field]')
        setTargetHeight(rect.height || 44)
        setSearchLayoutVariables(field instanceof HTMLElement ? readSearchLayoutVariables(field) : {})
        setMaxHeight(Math.max(160, window.innerHeight - rect.top - 16))

        const closeIfOutside = (event: Event) => {
            const eventTarget = event.target
            if (!(eventTarget instanceof Node)) {
                closeSearch()
                return
            }
            if (anchor.contains(eventTarget) || dropdownRef.current?.contains(eventTarget)) return
            closeSearch()
        }

        // Mantine closes on outside click. Scroll does not bubble, and it has no outside-focus option.
        window.addEventListener('scroll', closeIfOutside, true)
        document.addEventListener('focusin', closeIfOutside)
        return () => {
            window.removeEventListener('scroll', closeIfOutside, true)
            document.removeEventListener('focusin', closeIfOutside)
        }
    }, [closeSearch, search.isOpen])

    const handleDropdownKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        const anchor = targetRef.current
        const dropdown = dropdownRef.current
        if (event.key !== 'Tab' || !anchor || !dropdown) return

        const dropdownTabbables = Array.from(dropdown.querySelectorAll<HTMLElement>(TABBABLE_SELECTOR)).filter(
            isTabbable,
        )
        const edge = event.shiftKey ? dropdownTabbables[0] : dropdownTabbables[dropdownTabbables.length - 1]
        if (document.activeElement !== edge) return

        const destination = findTabbableAroundAnchor(anchor, dropdown, event.shiftKey)
        if (!destination) return

        event.preventDefault()
        destination.focus()
    }

    return (
        <Popover
            opened={search.isOpen}
            onChange={(nextOpened) => {
                if (!nextOpened) {
                    search.close()
                }
            }}
            position="bottom-start"
            offset={{ mainAxis: -targetHeight, crossAxis: 0 }}
            positionDependencies={[targetHeight, maxHeight]}
            width="target"
            withinPortal
            floatingStrategy="fixed"
            withArrow={false}
            shadow="none"
            withOverlay
            overlayProps={{
                fixed: true,
                zIndex: 1999,
                className: classes.desktopSearchScrim,
            }}
            trapFocus={false}
            returnFocus={false}
            zIndex={2000}
            middlewares={{ flip: false, shift: false }}
            transitionProps={{ duration: 0 }}
        >
            <Popover.Target>
                <div ref={targetRef} className={classes.popoverTarget}>
                    {target}
                </div>
            </Popover.Target>

            <Popover.Dropdown
                ref={dropdownRef}
                className={classes.desktopConnectedPanel}
                onKeyDown={handleDropdownKeyDown}
                style={
                    {
                        maxHeight,
                        ...searchLayoutVariables,
                    } as React.CSSProperties
                }
            >
                <SearchInputField
                    variant="inlineOpen"
                    value={search.query}
                    placeholder={placeholder}
                    ariaLabel={ariaLabel}
                    onChange={search.setQuery}
                    onKeyDown={onKeyDown}
                    inputRef={search.inputRef}
                    autoFocus
                    isLoading={search.isInputLoading}
                />
                {!search.isSuggestionListHidden && (
                    <div
                        className={`${classes.desktopSearchSuggestions} ${termSuggestions.length === 0 && search.query.length === 0 ? classes.desktopSearchSuggestionsEmptyState : ''}`}
                    >
                        <SearchSuggestionPanel
                            query={search.query}
                            termSuggestions={termSuggestions}
                            searchSuggestions={search.searchSuggestions}
                            isTypeaheadPending={search.isTypeaheadPending}
                            onTermSelect={search.selectTermSuggestion}
                            onSubmit={() => search.submit(undefined, 'search_query_click')}
                        />
                    </div>
                )}
            </Popover.Dropdown>
        </Popover>
    )
}
